import * as React from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useNylasAttachmentDownload } from '@/hooks/use-nylas-attachments';

type Attachment = {
  id: string;
  name: string;
  size: number;
  url?: string;
};

type AttachmentsProps = {
  attachments: Attachment[];
  emailGrantId?: string;
  emailMessageId?: string;
};

function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

export function MessageAttachments({ attachments, emailGrantId, emailMessageId }: AttachmentsProps) {
  const { mutate: downloadAttachment, isPending } = useNylasAttachmentDownload();

  if (attachments.length === 0)
    return null;

  const handleDownload = (attachmentId: string, filename: string) => {
    // If we have a direct URL, use it
    const attachment = attachments.find((att) => att.id === attachmentId);
    if (attachment?.url) {
      window.open(attachment.url, '_blank');
      return;
    }

    // Otherwise, use our Nylas attachment download endpoint
    if (emailGrantId && emailMessageId) {
      downloadAttachment({
        grantId: emailGrantId,
        attachmentId,
        filename,
        messageId: emailMessageId,
      });
    }
  };

  return (
    <>
      <Separator className="my-4" />
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Attachments</h4>
        {attachments.map((file) => (
          <div
            key={file.id}
            className="flex items-center gap-2 rounded-md border bg-muted/40 p-2 text-sm"
          >
            <div className="flex-1 truncate">{file.name}</div>
            <div className="text-xs text-muted-foreground">
              {formatFileSize(file.size)}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="size-6 p-0"
              onClick={() => handleDownload(file.id, file.name)}
              title="Download attachment"
              disabled={isPending}
            >
              <Download className="size-3" />
            </Button>
          </div>
        ))}
      </div>
    </>
  );
}
